import moment from 'moment/moment'
import { Daycare, DaycareOpening, DaycareType } from '@/types/Daycare'
import { ClosingDay } from '@/types/ClosingDays'
import { Moment } from 'moment-timezone'
import { Role } from '@/types/Role'
import { dataRegularSlots } from './eventsRegularSlots'
import { dataRequestSlots } from './eventsRequestSlots'
import { dataClosingDays } from './eventsClosingDays'
import { dataWorks } from './eventsWorks'
import { dataEventDays } from './eventsEventDays'
import { dataOccasionalSlots } from './eventsOccasionalSlots'
import { AllSlots, CalendarEventType, EventCalendar } from '@/types/Calendar'
import { DateRateSlot } from '@/types/StaffRate'
import { dataTaskNotes } from './eventsTaskNotes.ts'

export enum CalendarView {
    Current = 'customThreeWeeks',
    OneDay = 'customOneDay',
    OneWeek = 'customOneWeek',
    OneMonth = 'customOneMonth',
    Agenda = 'customListDay'
}

type calendarViewsType = {
    customThreeWeeks: {
        type: string
        dayMaxEvents: boolean
        duration: {
            weeks: number
        }
        firstDay: number
    }
    customOneDay: {
        type: string
        duration: {
            days: number
        }
        allDaySlot: boolean
        nowIndicator: boolean
        now: Date
        slotEventOverlap: boolean
        showNonCurrentDates: boolean
    }
    customOneWeek: {
        type: string
        dayMaxEvents: boolean
        showNonCurrentDates: boolean
        fixedWeekCount: boolean
    }
    customOneMonth: {
        type: string
        dayMaxEvents: number
        showNonCurrentDates: boolean
        fixedWeekCount: boolean
    }
    customListDay: {
        type: string
        dayMaxEvents: boolean
        showNonCurrentDates: boolean
        fixedWeekCount: boolean
    }
}

export const calendarViews: calendarViewsType = {
    customThreeWeeks: {
        type: 'dayGrid',
        dayMaxEvents: true,
        duration: { weeks: 3 },
        firstDay: 1
    },
    customOneDay: {
        type: 'timeGridWeek',
        duration: { days: 1 },
        allDaySlot: false,
        nowIndicator: true,
        now: moment().toDate(),
        slotEventOverlap: false,
        showNonCurrentDates: false
    },
    customOneWeek: {
        type: 'dayGridWeek',
        dayMaxEvents: true,
        showNonCurrentDates: false,
        fixedWeekCount: false
    },
    customOneMonth: {
        type: 'dayGridMonth',
        dayMaxEvents: 2,
        showNonCurrentDates: false,
        fixedWeekCount: false
    },
    customListDay: {
        type: 'listMonth',
        dayMaxEvents: true,
        showNonCurrentDates: false,
        fixedWeekCount: false
    }
}

function rateSlotsToCalendarEvents(data: DateRateSlot[]): EventCalendar[] {
    return data.map((rateSlot: DateRateSlot) => {
        const start: string = moment(rateSlot.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(rateSlot.end, 'HH:mm:ss').format('HH:mm')

        return {
            title: `${rateSlot.sectionGroupName ? rateSlot.sectionGroupName : ''}`,
            start: moment(
                moment(rateSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + start,
                'YYYY-MM-DD HH:mm'
            ).toDate(),
            end: moment(
                moment(rateSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + end,
                'YYYY-MM-DD HH:mm'
            ).toDate(),
            number: '',
            type: CalendarEventType.RATE_SLOT,
            sectionGroupId: rateSlot.sectionGroupId,
            slots: rateSlot.rate
        }
    })
}

export function rateSlotsDataToCalendarEvents(data: DateRateSlot[]): EventCalendar[] {
    return data && data.length > 0 ? rateSlotsToCalendarEvents(data) : []
}

export function dataToCalendarEvents(
    data: AllSlots,
    datesClosingDays: Moment[],
    daycare: Daycare,
    role: Role
): EventCalendar[] {
    const daycareType: DaycareType = daycare.type

    const events: EventCalendar[] = []

    if (data.getSlots.OccasionalSlots && data.getSlots.OccasionalSlots.length > 0) {
        dataOccasionalSlots(data.getSlots.OccasionalSlots, datesClosingDays, daycareType, role).map(
            (occasionalSlot: EventCalendar) => events.push(occasionalSlot)
        )
    }

    if (data.getSlots.RegularSlots && data.getSlots.RegularSlots.length > 0) {
        dataRegularSlots(data.getSlots.RegularSlots, datesClosingDays, daycareType, role).map(
            (regularSlot: EventCalendar) => events.push(regularSlot)
        )
    }

    if (data.getSlots.RequestSlots && data.getSlots.RequestSlots.length > 0) {
        dataRequestSlots(data.getSlots.RequestSlots, datesClosingDays, daycareType).map((requestSlot: EventCalendar) =>
            events.push(requestSlot)
        )
    }

    if (data.getWorks && data.getWorks.length > 0) {
        dataWorks(data.getWorks).map((work: EventCalendar) => events.push(work))
    }

    if (data.getEventDay && data.getEventDay.length > 0) {
        dataEventDays(data.getEventDay, datesClosingDays).map((eventDay: EventCalendar) => events.push(eventDay))
    }

    if (data.getClosingDay && data.getClosingDay.length > 0) {
        dataClosingDays(data.getClosingDay).map((closingDay: EventCalendar) => events.push(closingDay))
    }

    if (data.getTaskNotesByDaycare && data.getTaskNotesByDaycare.length > 0) {
        dataTaskNotes(data.getTaskNotesByDaycare).map((taskNote: EventCalendar) => events.push(taskNote))
    }

    return events
}

export function getDateClosingDays(closingDays: ClosingDay[]): Moment[] {
    const dates: Moment[] = []

    if (closingDays && closingDays.length > 0) {
        closingDays.forEach((closingDay: ClosingDay) => {
            const startDate: Moment = moment(closingDay.startDate, 'DD-MM-YYYY')
            const endDate: Moment = moment(closingDay.endDate, 'DD-MM-YYYY')

            while (startDate.isSameOrBefore(endDate)) {
                dates.push(startDate.clone())
                startDate.add(1, 'day')
            }
        })
    }

    return dates
}

export function getClosingDaycareDays(opening: DaycareOpening): number[] {
    const days: number[] = []

    if (opening) {
        if (!opening.monOpen) days.push(1)
        if (!opening.tueOpen) days.push(2)
        if (!opening.wedOpen) days.push(3)
        if (!opening.thuOpen) days.push(4)
        if (!opening.friOpen) days.push(5)
        if (!opening.satOpen) days.push(6)
        if (!opening.sunOpen) days.push(0)
    } else {
        return [0, 1, 2, 3, 4, 5, 6]
    }

    return days.sort((a: number, b: number) => a - b)
}
